import { catAppAPIClient } from "@/app/apiClient";
import { User } from "firebase/auth";
import { ErrUnauthorised } from "@/app/api/Errors";
import { SiteStatus } from "@anu-its/cat-grpc-pb/js/anu/cat/enums_pb";
import { formatErrorMessage } from "@/app/utils/errorHelpers";
import { getCurrentUser } from "@/app/firebase/auth";
import { timestampFromDate } from "@bufbuild/protobuf/wkt";
import { EnrolmentAttribute } from "@anu-its/cat-grpc-pb/js/anu/cat/attribute_pb";
import {
  GetSiteResponse,
  GetTeachingSitesRequest,
  GetTeachingSitesResponse,
} from "@anu-its/cat-grpc-pb/js/anu/cat_app/site_pb";
import {
  CreateSandpitSiteResponse,
  GetSandpitSitesRequest,
  GetSandpitSitesResponse,
} from "@anu-its/cat-grpc-pb/js/anu/cat_app/sandpit_site_pb";
import {
  GetParentSitesRequest,
  GetParentSitesResponse,
} from "@anu-its/cat-grpc-pb/js/anu/cat_app/parent_site_pb";
import {
  GetSASSitesRequest,
  GetSASSitesResponse,
} from "@anu-its/cat-grpc-pb/js/anu/cat_app/sas_site_pb";
import { CreateCommunitySiteRequest } from "@anu-its/cat-grpc-pb/js/anu/cat_app/community_site_pb";

async function GetSite(siteId: string): Promise<GetSiteResponse | Error> {
  const user = await getCurrentUser();
  if (!user) return ErrUnauthorised;

  try {
    const request = {
      siteId: siteId,
      includeRoles: true,
      includeChildren: true,
      includeParent: true,
    };

    return await catAppAPIClient.getSite(request);
  } catch (err) {
    console.error("GetSite failed:", err);
    throw new Error("Failed to get site. Please try again later.");
  }
}

async function GetSandpitSites(
  pageSize: number,
  cursor: string,
  statuses: SiteStatus[],
  owners?: string | string[],
): Promise<GetSandpitSitesResponse | Error> {
  const user = await getCurrentUser();
  if (!user) return ErrUnauthorised;

  try {
    const ownerList = owners ? (Array.isArray(owners) ? owners : [owners]) : [];
    const request = {
      pageSize: BigInt(pageSize),
      ...(cursor.length > 0
        ? { pagination: { case: "cursor", value: cursor } }
        : {}),
      owners: ownerList,
      status: statuses,
    } as GetSandpitSitesRequest;

    return await catAppAPIClient.getSandpitSites(request);
  } catch (err) {
    console.error("GetSandpitSites failed:", err);
    throw new Error("Failed to get site. Please try again later.");
  }
}

async function GetParentSites(
  pageSize: number,
  academicTerms: string[],
  courseQuery: string,
): Promise<GetParentSitesResponse | Error> {
  const user = await getCurrentUser();
  if (!user) return ErrUnauthorised;

  try {
    const request = {
      pageSize: BigInt(pageSize),
      academicTermCode: academicTerms,
      ...(courseQuery.length > 0 ? { query: courseQuery } : {}),
    } as GetParentSitesRequest;

    return await catAppAPIClient.getParentSites(request);
  } catch (err) {
    console.error("GetParentSites failed:", err);
    throw new Error("Failed to get site. Please try again later.");
  }
}

async function GetSASSites(
  pageSize: number,
  cursor: string,
  academicTerms: string[],
  statuses: SiteStatus[],
  courseQuery: string,
): Promise<GetSASSitesResponse | Error> {
  const user = await getCurrentUser();
  if (!user) return ErrUnauthorised;

  try {
    const request = {
      pageSize: BigInt(pageSize),
      academicTermCode: academicTerms,
      status: statuses,
      ...(courseQuery.length > 0 ? { query: courseQuery } : {}),
      ...(cursor.length > 0
        ? { pagination: { case: "cursor", value: cursor } }
        : {}),
    } as GetSASSitesRequest;

    return await catAppAPIClient.getSASSites(request);
  } catch (err) {
    console.error("GetSASSites failed:", err);
    throw new Error("Failed to get SAS sites. Please try again later.");
  }
}

async function GetTeachingSites(
  pageSize: number,
  cursor: string,
  academicTerms: string[],
  statuses: SiteStatus[],
  courseQuery: string,
  filterByAssignedRole: boolean,
): Promise<GetTeachingSitesResponse | Error> {
  const user = await getCurrentUser();
  if (!user) return ErrUnauthorised;

  try {
    const request = {
      pageSize: BigInt(pageSize),
      academicTermCode: academicTerms,
      status: statuses,
      ...(courseQuery.length > 0 ? { query: courseQuery } : {}),
      populateChildSiteDetails: false,
      filterByAssignedSiteRoles: filterByAssignedRole,
      ...(cursor.length > 0
        ? { pagination: { case: "cursor", value: cursor } }
        : {}),
    } as GetTeachingSitesRequest;

    return await catAppAPIClient.getTeachingSites(request);
  } catch (err) {
    console.error("GetTeachingSites failed:", err);
    throw new Error("Failed to get Teaching sites. Please try again later.");
  }
}

async function CreateSandpitSites({
  owner,
  templateSiteID,
}: {
  user: User;
  owner: string;
  templateSiteID?: string;
}): Promise<CreateSandpitSiteResponse | Error> {
  const user = await getCurrentUser();
  if (!user) return ErrUnauthorised;

  try {
    return await catAppAPIClient.createSandpitSite({
      owner: owner,
      templateSiteId: templateSiteID,
    });
  } catch (err) {
    console.error("Create sandpit sites failed:", err);
    if (err instanceof Error) {
      throw new Error(formatErrorMessage(err.message));
    } else {
      throw new Error("Failed to create sandpit. Please try again later.");
    }
  }
}

async function CreateCommunitySites({
  user,
  academicOrg,
  templateSiteID,
  collegeOrg,
  name,
  courseCode,
  academicTerm,
  startDate,
  endDate,
  owner,
  enrolmentAttributes,
}: {
  user: User;
  academicOrg: string;
  templateSiteID: string;
  collegeOrg: string;
  name: string;
  courseCode: string;
  academicTerm: string;
  startDate: Date | null;
  endDate: Date | null;
  owner: string;
  enrolmentAttributes: EnrolmentAttribute[];
}) {
  if (!user) return ErrUnauthorised;

  try {
    const user = await getCurrentUser();
    if (!user) return ErrUnauthorised;

    const request = {
      academicOrg: academicOrg,
      templateSiteId: templateSiteID,
      collegeOrg: collegeOrg,
      name: name,
      courseCode: courseCode,
      academicTerm: academicTerm,
      startDate: startDate ? timestampFromDate(startDate) : null,
      endDate: endDate ? timestampFromDate(endDate) : null,
      owner: owner,
      enrolmentAttributes: enrolmentAttributes as EnrolmentAttribute[],
    } as CreateCommunitySiteRequest;

    return await catAppAPIClient.createCommunitySite(request);
  } catch (err) {
    console.error("Create community sites failed:", err);
    if (err instanceof Error) {
      throw new Error(formatErrorMessage(err.message));
    } else {
      throw new Error(
        "Failed to create community site. Please try again later.",
      );
    }
  }
}

export {
  GetSite,
  GetSandpitSites,
  GetSASSites,
  GetParentSites,
  GetTeachingSites,
  CreateSandpitSites,
  CreateCommunitySites,
};
